import {IActionFulfilled} from 'models/common';
import {IFileDetails} from 'models/resources';
import {
  IUpdateFileResourceApiResponse,
  IUpdateScormResourceApiResponse,
  IUpdateWebLinkResourceApiResponse,
} from 'models/resources';
import {isObject} from 'utils/functions';
import {IFileDetailsStoreState} from './IFileDetailsStoreState';
import {
  deleteFileThunk,
  downloadFileByIdThunk,
  fetchFileByIdThunk,
  updateResourcesFileThunk,
  updateScormResourceThunk,
  updateWebLinkResourceThunk,
} from './thunks';

const extractUpdatedFileDetails = (payload: unknown): IFileDetails | null => {
  if (!isObject(payload)) {
    return null;
  }

  const body = payload as Record<string, unknown>;
  const nested = body.data;

  // Prefer wrapped API payloads: { data: IFileDetails }
  if (isObject(nested)) {
    const nestedDetails = (nested as unknown) as IFileDetails;
    if (nestedDetails.id || nestedDetails.metadata || nestedDetails.contentType !== undefined) {
      return nestedDetails;
    }
  }

  // Some endpoints return IFileDetails directly.
  const direct = (body as unknown) as IFileDetails;
  if (direct.id || direct.metadata || direct.contentType !== undefined) {
    return direct;
  }

  return null;
};

const applyUpdatedFileDetails = (
  state: IFileDetailsStoreState,
  payload: unknown,
  options?: {clearFileAttachment?: boolean}
) => {
  const updated = extractUpdatedFileDetails(payload);
  if (!updated) {
    state.page.loading = false;
    return;
  }

  state.fileDetails = {
    ...state.fileDetails,
    ...updated,
    ...(options?.clearFileAttachment ? {file: null} : {}),
  };
  state.page.formRevision += 1;
  state.page.loading = false;
};

export const fileDetailsExtraReducer = {
  // fetchFileByIdThunk
  [fetchFileByIdThunk.fulfilled]: (state: IFileDetailsStoreState, action: IActionFulfilled<null, any>) => {
    if (isObject(action.payload)) {
      const {data} = action.payload;
      state.fileDetails = data;
    }
    state.page.loading = false;
  },
  [fetchFileByIdThunk.pending]: (state: IFileDetailsStoreState) => {
    state.fileDetails = null;
    state.page.loading = true;
  },
  [fetchFileByIdThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },

  // deleteFileThunk
  [deleteFileThunk.fulfilled]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },
  [deleteFileThunk.pending]: (state: IFileDetailsStoreState) => {
    state.page.loading = true;
  },
  [deleteFileThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },

  // updateResourcesFileThunk
  [updateResourcesFileThunk.fulfilled]: (
    state: IFileDetailsStoreState,
    action: IActionFulfilled<null, IUpdateFileResourceApiResponse>
  ) => {
    applyUpdatedFileDetails(state, action.payload, {clearFileAttachment: true});
  },
  [updateResourcesFileThunk.pending]: (state: IFileDetailsStoreState) => {
    state.page.loading = true;
  },
  [updateResourcesFileThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },

  // updateScormResourceThunk
  [updateScormResourceThunk.fulfilled]: (
    state: IFileDetailsStoreState,
    action: IActionFulfilled<null, IUpdateScormResourceApiResponse>
  ) => {
    applyUpdatedFileDetails(state, action.payload);
  },
  [updateScormResourceThunk.pending]: (state: IFileDetailsStoreState) => {
    state.page.loading = true;
  },
  [updateScormResourceThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },

  // updateWebLinkResourceThunk
  [updateWebLinkResourceThunk.fulfilled]: (
    state: IFileDetailsStoreState,
    action: IActionFulfilled<null, IUpdateWebLinkResourceApiResponse>
  ) => {
    applyUpdatedFileDetails(state, action.payload);
  },
  [updateWebLinkResourceThunk.pending]: (state: IFileDetailsStoreState) => {
    state.page.loading = true;
  },
  [updateWebLinkResourceThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.loading = false;
  },

  // downloadFileByIdThunk
  [downloadFileByIdThunk.fulfilled]: (state: IFileDetailsStoreState) => {
    state.page.fileDownloading = false;
  },
  [downloadFileByIdThunk.pending]: (state: IFileDetailsStoreState) => {
    state.page.fileDownloading = true;
  },
  [downloadFileByIdThunk.rejected]: (state: IFileDetailsStoreState) => {
    state.page.fileDownloading = false;
  },
};
